import React from 'react';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Dashboard, DashboardConfiguration } from 'types/dashboard';
import { DashboardConfigurationSchema } from 'types/dashboard_schema';

import { readFile } from 'services/files';
import { useAPI } from 'hooks/api/useAPI';
import { useCreateDashboard } from 'dashboards/hooks/useCreateDashboard';
import { useCreateDashboardFromTemplate } from 'dashboards/hooks/useCreateDashboardFromTemplate';
import { useDashboardTemplates } from 'dashboards/hooks/useDashboardTemplates';
import { useModal } from 'hooks/useModal';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';

import * as Dialog from 'components/Dialog';
import { Button } from 'components/Button/Button';
import { Dropzone } from 'components/Dropzone/Dropzone';
import { Field } from 'components/Field/Field';
import { FileDisplay } from 'components/FileDisplay/FileDisplay';
import { FormErrorMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { Input } from 'components/Input/Input';
import { Select } from 'components/Select/Select';

type CreateDashboardFieldValues = {
  name: string;
  template: string | null;
  file: File | null;
};

type TemplateOption = {
  label: string;
  value: string;
};

type CreateDashboardDialogProps = Pick<Dialog.RootProps, 'open'>;

export function CreateDashboardDialog({ open }: CreateDashboardDialogProps) {
  const { close } = useModal();

  return (
    <Dialog.Root
      open={open}
      className="w-[40rem]"
      onOpenChange={(open) => !open && close()}
    >
      <Dialog.Title asChild>
        <Heading level="2">Create new dashboard</Heading>
      </Dialog.Title>
      <CreateDashboardDialogContent />
    </Dialog.Root>
  );
}

function CreateDashboardDialogContent() {
  const navigate = useNavigate();
  const { applicationID } = useAPI();
  const { close } = useModal();
  const [templateQuery, setTemplateQuery] = React.useState('');
  const [debouncedTemplateQuery] = useDebouncedValue(templateQuery, {
    wait: 250,
  });

  const { preset, start, stop, timezone } = useTimeRangeParams();
  const search = new URLSearchParams({
    preset,
    start,
    stop,
    timezone,
  }).toString();

  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = useForm<CreateDashboardFieldValues>();

  function handleDashboardCreated(dashboard: Dashboard) {
    navigate({
      pathname: `/applications/${applicationID}/monitor/dashboards/${dashboard.id}`,
      search,
    });
    close();
  }

  const {
    isLoading: isCreatingDashboard,
    error,
    mutate: createDashboard,
  } = useCreateDashboard({
    onSuccess: handleDashboardCreated,
  });

  const {
    error: createDashboardFromTemplateError,
    isLoading: isCreatingFromTemplate,
    mutate: createDashboardFromTemplate,
  } = useCreateDashboardFromTemplate({
    onSuccess: handleDashboardCreated,
  });

  const {
    data: dashboardTemplates,
    error: dashboardTemplatesError,
    isLoading: isLoadingDashboardTemplates,
  } = useDashboardTemplates({
    params: {
      only_included_in_plan: true,
      query: debouncedTemplateQuery.trim() || undefined,
    },
  });

  async function createDashboardFromJsonFile({
    name,
    file,
  }: Omit<CreateDashboardFieldValues, 'template'>) {
    if (!file) {
      return;
    }

    let content;

    try {
      content = await readFile(file);
    } catch {
      setError('file', { message: 'Failed to read file.' });
      return;
    }

    let json;

    try {
      json = JSON.parse(content);
    } catch {
      setError('file', { message: 'Failed to parse json.' });
      return;
    }

    let dashboardConfiguration: DashboardConfiguration;

    try {
      dashboardConfiguration = DashboardConfigurationSchema.parse(json);
    } catch {
      setError('file', {
        message: 'The dashboard configuration is not valid.',
      });
      return;
    }

    createDashboard({
      name,
      configuration: dashboardConfiguration,
    });
  }

  async function onSubmit({
    name,
    template,
    file,
  }: CreateDashboardFieldValues) {
    if (template === 'import') {
      await createDashboardFromJsonFile({ name, file });
      return;
    }

    if (template && template !== 'blank') {
      const dashboardTemplate = dashboardTemplates?.find(
        ({ id }) => id === template
      );

      if (!dashboardTemplate) {
        setError('template', {
          message: 'Failed to load the selected dashboard template.',
        });
        return;
      }

      createDashboardFromTemplate({ name, template: dashboardTemplate });
      return;
    }

    createDashboard({
      name,
      configuration: { widgets: [] },
    });
  }

  function handleDeleteFile() {
    setValue('file', null);
    clearErrors('file');
  }

  const TEMPLATE_OPTIONS = React.useMemo<TemplateOption[]>(
    () => [
      { label: 'Blank', value: 'blank' },
      { label: 'Import from JSON', value: 'import' },
      ...(dashboardTemplates?.map(({ id, name }) => ({
        label: name,
        value: id,
      })) ?? []),
    ],
    [dashboardTemplates]
  );

  const currentFile = watch('file');
  const currentTemplate = watch('template');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Field label="Template" error={errors.template} required>
        <Controller
          name="template"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              value={
                value
                  ? TEMPLATE_OPTIONS.find((opt) => opt.value === value) || null
                  : null
              }
              onChange={(option) => {
                onChange(option ? option.value : option);

                // Set dashboard name to template name if empty and not blank/import
                if (
                  option &&
                  !getValues('name') &&
                  option.value !== 'blank' &&
                  option.value !== 'import'
                ) {
                  setValue('name', option.label);
                }

                // Clear file when switching templates
                setValue('file', null);
                clearErrors('file');
              }}
              options={TEMPLATE_OPTIONS}
              onInputChange={(value, { action }) => {
                if (action === 'input-change') {
                  setTemplateQuery(value);
                }
              }}
              isLoading={isLoadingDashboardTemplates}
              isClearable={false}
              autoFocus
            />
          )}
        />
      </Field>

      {currentTemplate && (
        <Field label="Dashboard name" error={errors.name} required>
          <Input
            {...register('name', {
              required: 'Please choose a name for this dashboard.',
            })}
            type="text"
            autoComplete="off"
            spellCheck="false"
          />
        </Field>
      )}

      {currentTemplate === 'import' && (
        <Field label="Import from JSON" error={errors.file} required>
          <Controller
            name="file"
            control={control}
            render={({ field: { onChange } }) => (
              <Dropzone
                onChange={([file]) => {
                  if (file) {
                    onChange(file);
                  }
                }}
                accept="application/json"
              />
            )}
          />

          {currentFile && (
            <FileDisplay
              file={currentFile}
              onDelete={handleDeleteFile}
              isError={Boolean(errors.file)}
            />
          )}
        </Field>
      )}

      <FormErrorMessage
        error={
          error || dashboardTemplatesError || createDashboardFromTemplateError
        }
      />

      <div className="flex justify-end gap-2">
        <Dialog.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Dialog.Close>
        <Button
          variant="primary"
          type="submit"
          loading={isCreatingDashboard || isCreatingFromTemplate}
        >
          Create
        </Button>
      </div>
    </form>
  );
}
